import type {
  GlobalCustomersDocumentApprovalDto,
  GlobalCustomersDocumentApprovalDtoApprovalState,
} from '@ori/global-api-client-react';
import type { FC, MouseEvent, ReactElement } from 'react';

import type { IDocumentsInfo } from '../../interfaces';
import { useCustomerDocument, useRegistrationData } from '../../models/api';
import { DocumentState } from '../Document/DocumentState';
import {
  StyledSpanFile,
  StyledSpanFileName,
  StyledSpanFileNameLink,
  StyledSpanFileSize,
} from './DocumentItemStyledComponents';

interface IDocumentItemFileProps {
  document: IDocumentsInfo;
  visibleApprovalState?: boolean;
}

const bytesInKb = 1024;

export const DocumentItemFile: FC<IDocumentItemFileProps> = (props) => {
  const { document, visibleApprovalState } = props;

  const { data: registrationData } = useRegistrationData();

  const customerId = registrationData?.customerId ?? null;

  const documentItemUrl = useCustomerDocument(customerId, document.documentId);

  const handleDownloadDocument = (event: MouseEvent) => {
    const link = event.target as HTMLLinkElement;

    if (!link.href) {
      event.preventDefault();

      const documentUrl = documentItemUrl ?? '';
      if (documentUrl !== '') {
        link.href = documentUrl;
        link.click();
      }
    }
  };

  const getDocumentItemLink = () => {
    const { fileName, documentId }: IDocumentsInfo = document;

    if (fileName && documentId && documentId < 0) {
      return <StyledSpanFileName>{fileName}</StyledSpanFileName>;
    }

    if (fileName) {
      return <StyledSpanFileNameLink onClick={handleDownloadDocument}>{fileName}</StyledSpanFileNameLink>;
    }

    return null;
  };

  const getDocumentSize = () => {
    const { fileSize }: IDocumentsInfo = document;
    const hundred = 100;
    const size = Math.round((fileSize ? fileSize / bytesInKb / bytesInKb : 0) * hundred) / hundred;

    return <StyledSpanFileSize>{size} MB</StyledSpanFileSize>;
  };

  const getApprovalState = (approval?: GlobalCustomersDocumentApprovalDto): ReactElement | null => {
    if (visibleApprovalState) {
      // if there is no approval information in document data,
      // it is automatically in "Pending" state
      let approvalState: GlobalCustomersDocumentApprovalDtoApprovalState = 'Pending';
      let approvalReason;

      if (approval?.approvalState) {
        const { approvalState: state, reason }: GlobalCustomersDocumentApprovalDto = approval;
        approvalState = state;
        approvalReason = reason;
      }

      return (
        <DocumentState
          state={approvalState}
          reason={approvalReason}
        />
      );
    }

    return null;
  };

  return (
    <StyledSpanFile>
      {getDocumentItemLink()}
      {getDocumentSize()}
      {getApprovalState(document.approval)}
    </StyledSpanFile>
  );
};
